import { queryOptions } from "@tanstack/react-query"
import { api } from "./api"
import { isV2Api } from "./api-contract"
import { getSessionUser } from "./auth"
import type { RichTextDoc } from "./rich-text"
import type { FileAttachment } from "./attachments"
import {
  archiveEventConversation,
  createEventConversation,
  listEventConversationMembers,
  listEventConversations,
  markEventConversationRead,
  patchEventConversation,
  postEventConversationMessage,
  setEventConversationMembers,
} from "./v2/organizer"
import type { V2ConversationMessage, V2ConversationSummary } from "./v2/contracts"

// Channels data layer — channels are `conversations` rows with scope='channel'.
// Mirrors the admin SPA's ChannelsPage endpoints. CRITICAL: the query keys here
// match admin/tickets byte-for-byte (`["resource", <table>, <filters>]`) so the
// vendored realtime layer (realtime.ts → cache.ts) surgically patches these
// caches on live CRUD events with no extra wiring:
//   • conversations list   → ["resource", "conversations", { eventId, scope }]
//   • a channel's messages → ["resource", "chat-messages", { conversationId }]
// chat-messages INSERT frames are spliced into the open thread (DESC → prepend)
// by tryMessageInsertSplice; conversation UPDATE/DELETE frames patch the list.

export type Conversation = {
  id: string
  eventId: string
  scope: string
  name: string | null
  topic: string | null
  emoji: string | null
  agendaItemId: string | null
  agendaGroupId: string | null
  attendeeGroupId: string | null
  cohortKind?: string | null
  cohortKey?: string | null
  includesAllConfirmedGuests: number
  postingMode: string
  // Internal channels only: "all" (every staffer) | "selected" (explicit roster).
  // null/absent == "all" for back-compat.
  staffAudience?: string | null
  pinnedAtTop: number
  archivedAt: string | null
  createdAt: string
  modifiedAt: string | null
  lastMessage?: ConversationLastMessage | null
  unread?: number
  // True when an unread message in this conversation @-mentions me (server-
  // computed by list-my-conversations). Drives the personal "@Me" indicator.
  mentionsMe?: boolean
  canPost?: boolean
}

export type ConversationLastMessage = {
  id: string
  authorName: string | null
  authorRole: string | null
  body: string | null
  kind: string
  createdAt: string
}

export type ChatMessage = {
  id: string
  conversationId: string
  // NULL when a sessionless attendee authored the message (guest path) — the
  // server then sets `authorGuestId` instead. `authorNameAtSend` is always set.
  authorUserId: string | null
  authorGuestId?: string | null
  authorNameAtSend: string
  authorRoleAtSend: string | null
  body: string | null
  // Constrained Tiptap doc (JSON string) derived server-side. Null for legacy /
  // trivial messages — RichText falls back to `body`.
  richText?: string | null
  // JSON array of attachments (file/image), stored verbatim + returned on read.
  // Null when none. Parsed by parseMessageAttachments.
  attachments?: string | null
  kind: string
  urgency?: "normal" | "urgent" | string | null
  createdAt: string
  // Edit/delete lifecycle — server columns on chat_messages. editedAt stamped
  // by editMessage; deletedAt by deleteMessage (soft delete). Null when neither.
  editedAt?: string | null
  deletedAt?: string | null
  // JSON array of resolved @-mentions; raw JSON, parsed downstream. Null = none.
  mentions?: string | null
  // JSON array of link unfurl cards; written async (waitUntil) post-send and
  // re-broadcast as an `edited` frame. Raw JSON, rendered by LinkUnfurl. Null = none.
  unfurls?: string | null
  // Threading — server columns on chat_messages. parentMessageId set on replies;
  // replyCount/lastReplyAt bumped on the root. Null on root rows with no replies.
  parentMessageId?: string | null
  replyCount?: number | null
  lastReplyAt?: string | null
  // Back-reference for a thread reply that was ALSO posted to the main stream
  // ("Also post"): the thread root id + a short preview of the root, used to
  // render the "Replied to a thread" header on the reflected copy. Distinct
  // from parentMessageId (which threads).
  reflectedFromThreadRootId?: string | null
  reflectedFromThreadPreview?: string | null
  // Client-only optimistic placeholder; cleared when the server echo reconciles.
  pending?: boolean
  clientId?: string | null
}

export type ConversationMember = {
  id: string
  conversationId: string
  userId: string
  role: string | null
}

export type AttendeeGroupLite = {
  id: string
  name: string
  visibility: string
  isAutomatic: boolean
  memberCount: number
}

type ListEnvelope<T> = {
  data: T[]
  meta?: { total?: number }
  pagination?: { hasMore?: boolean; count?: number; page?: number; pageSize?: number }
}
type ConversationInboxEnvelope = { success: true; conversations: ConversationInboxRow[] }
type ConversationInboxRow = Omit<
  Conversation,
  "includesAllConfirmedGuests" | "pinnedAtTop" | "attendeeGroupId"
> & {
  attendeeGroupId?: string | null
  includesAllConfirmedGuests: boolean | number
  pinnedAtTop: boolean | number
  lastMessage?: ConversationLastMessage | null
  unread?: number
  canPost?: boolean
}

// ── query keys (must match the realtime splice predicates) ───────────────────
export const channelsKey = (eventId: string) =>
  ["resource", "conversations", { eventId, scope: "channel" }] as const
export const channelMessagesKey = (conversationId: string) =>
  ["resource", "chat-messages", { conversationId }] as const
export const channelMembersKey = (conversationId: string) =>
  ["resource", "conversation-members", { conversationId }] as const

// ── query options ────────────────────────────────────────────────────────────
export function channelsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: channelsKey(eventId),
    queryFn: () => isV2Api
      ? listEventConversations(eventId).then((response): ConversationInboxEnvelope => ({
          success: true,
          conversations: response.conversations.map(v2ConversationToInboxRow),
        }))
      : api<ConversationInboxEnvelope>(`/api/v1/event/${eventId}/chat/list-my-conversations`, {
          method: "POST",
          body: {},
        }),
    select: (r): Conversation[] =>
      r.conversations
        .filter((c) => c.scope === "channel")
        .map((c) => ({
          ...c,
          attendeeGroupId: c.attendeeGroupId ?? null,
          includesAllConfirmedGuests: c.includesAllConfirmedGuests ? 1 : 0,
          pinnedAtTop: c.pinnedAtTop ? 1 : 0,
          createdAt: c.createdAt ?? c.lastMessage?.createdAt ?? "",
          modifiedAt: c.modifiedAt ?? c.lastMessage?.createdAt ?? c.createdAt ?? null,
          unread: c.unread ?? 0,
          lastMessage: c.lastMessage ?? null,
        })),
  })
}

// NOTE: channel messages are fetched via the same `list-conversation-messages`
// action as DMs (channels are conversations with scope='channel'; the action
// gates by membership, which always admits staff). ChannelThread imports
// conversationMessagesQueryOptions / fetchConversationMessagesPage directly so
// channels get the bundled reactions + cursor pagination for free. The old raw
// /chat-messages CRUD fetch (offset-based, no reactions) is retired.

export function channelMembersQueryOptions(eventId: string, conversationId: string) {
  return queryOptions({
    queryKey: channelMembersKey(conversationId),
    queryFn: () => isV2Api
      ? listEventConversationMembers(eventId, conversationId).then((response) => ({
          data: response.members.map((member) => ({
            id: member.id,
            conversationId: member.conversationId,
            userId: member.personId,
            role: member.role,
          })),
        }))
      : api<ListEnvelope<ConversationMember>>("/api/v1/conversation-members", {
          searchParams: { limit: 100, conversationId },
        }),
    select: (r) => r.data,
  })
}

// ── posting mode (matches conversations.postingMode) ─────────────────────────
// Stored values are the same regardless of audience ("members" | "organizers-only");
// only the LABELS + framing differ. Guest-facing channels frame the open mode as
// "Everyone" and the locked mode as "read-only for guests"; internal/staff
// channels have no guests, so they read "Members" / "Organizers only".
export type PostingMode = "members" | "organizers-only"
export type PostingModeOption = { value: PostingMode; label: string }

export function postingModesFor(guestFacing: boolean): PostingModeOption[] {
  return guestFacing
    ? [
        { value: "members", label: "Everyone — anyone can post" },
        { value: "organizers-only", label: "Organizers only — read-only for guests" },
      ]
    : [
        { value: "members", label: "Members — anyone in the channel can post" },
        { value: "organizers-only", label: "Organizers only" },
      ]
}

// ── mutations ────────────────────────────────────────────────────────────────
export type PostChannelMessageInput = {
  conversationId: string
  eventId: string
  body: string
  urgency: "normal" | "urgent"
  clientId: string
  richTextDoc?: RichTextDoc
  // Uploaded file attachments (presigned-R2 mediaIds). Empty/omitted = none.
  attachments?: FileAttachment[]
  // Set on a thread REPLY — the server links it under this root and bumps the
  // root's replyCount/lastReplyAt. Omitted = a top-level message.
  parentMessageId?: string
  // Set on a top-level "Also post" copy of a thread reply: the thread root id
  // this copy reflects (NOT parentMessageId — the copy must stay top-level so it
  // shows in the main stream). Forwarded so the backend can persist it once a
  // column lands; ignored server-side today. Drives the "Replied to a thread" row.
  reflectedFromThreadRootId?: string
}

export async function postChannelMessage(
  input: PostChannelMessageInput,
): Promise<{ message: ChatMessage; memberCount?: number }> {
  if (isV2Api) {
    const response = await postEventConversationMessage({
      eventId: input.eventId,
      conversationId: input.conversationId,
      body: input.body,
      replyToMessageId: input.parentMessageId,
      richTextDoc: input.richTextDoc,
      attachmentManifest: input.attachments,
      urgency: input.urgency,
      clientId: input.clientId,
      reflectedFromThreadRootId: input.reflectedFromThreadRootId,
    })
    return { message: v2MessageToChatMessage(response.message) }
  }
  return api<{ message: ChatMessage; memberCount?: number }>(
    `/api/v1/event/${input.eventId}/chat/post-message`,
    {
      method: "POST",
      body: {
        conversationId: input.conversationId,
        body: input.body,
        urgency: input.urgency,
        clientId: input.clientId,
        ...(input.richTextDoc ? { richTextDoc: input.richTextDoc } : {}),
        ...(input.attachments && input.attachments.length > 0
          ? { attachments: input.attachments }
          : {}),
        ...(input.parentMessageId ? { parentMessageId: input.parentMessageId } : {}),
        ...(input.reflectedFromThreadRootId
          ? { reflectedFromThreadRootId: input.reflectedFromThreadRootId }
          : {}),
        dryRun: false,
      },
    },
  )
}

export type CreateChannelInput = {
  eventId: string
  name: string | null
  topic: string | null
  emoji: string | null
  postingMode: string
  // Membership source — exactly one of these resolves how attendees join.
  includesAllConfirmedGuests: boolean
  attendeeGroupId: string | null
  agendaItemId: string | null
  agendaGroupId: string | null
  // Travel-cohort attachment — set BOTH together (flight or transport). See
  // suggest{Flight,Transport}Cohorts for the cohortKey shapes.
  cohortKind?: CohortKind | null
  cohortKey?: string | null
  // Internal channels: "all" staff or a "selected" roster (memberUserIds).
  staffAudience?: "all" | "selected"
  memberUserIds?: string[]
}

export type CohortKind =
  | "flight_arrival"
  | "flight_departure"
  | "arrival_window"
  | "departure_window"
  | "ground_transfer"
  | "transfer_window"

export async function createChannel(input: CreateChannelInput): Promise<{ id: string }> {
  if (isV2Api) {
    const title = [input.emoji, input.name].filter(Boolean).join(" ").trim()
    const response = await createEventConversation({
      eventId: input.eventId,
      kind: input.postingMode === "organizers-only" ? "announcement" : "channel",
      title: title || "Channel",
      topic: input.topic ?? undefined,
      originActivityId: input.agendaItemId ?? undefined,
      staffPersonIds: input.memberUserIds,
      staffAudience: input.staffAudience ?? "all",
      segmentIds: input.attendeeGroupId ? [input.attendeeGroupId] : [],
      choiceGroupIds: input.agendaGroupId ? [input.agendaGroupId] : [],
      cohort:
        input.cohortKind && input.cohortKey
          ? { kind: input.cohortKind, key: input.cohortKey }
          : null,
      includeAllConfirmedAttendees: input.includesAllConfirmedGuests,
      postingMode:
        input.postingMode === "organizers-only" ? "organizers-only" : "members",
      emoji: input.emoji,
    })
    return { id: response.conversation.id }
  }
  // Dedicated action (NOT raw CRUD): it seeds conversation_members for
  // select-staff channels. eventId is in the path; booleans, not 0/1.
  const r = await api<{ committed: boolean; id: string }>(
    `/api/v1/event/${input.eventId}/chat/create-channel`,
    {
      method: "POST",
      body: {
        name: input.name ?? "",
        topic: input.topic ?? undefined,
        emoji: input.emoji ?? undefined,
        postingMode: input.postingMode,
        agendaItemId: input.agendaItemId ?? undefined,
        agendaGroupId: input.agendaGroupId ?? undefined,
        attendeeGroupId: input.attendeeGroupId ?? undefined,
        includesAllConfirmedGuests: input.includesAllConfirmedGuests,
        cohortKind: input.cohortKind ?? undefined,
        cohortKey: input.cohortKey ?? undefined,
        staffAudience: input.staffAudience ?? "all",
        memberUserIds: input.memberUserIds ?? [],
      },
    },
  )
  return { id: r.id }
}

// ── internal-channel staff roster ────────────────────────────────────────────

export type EventStaff = {
  userId: string
  name: string | null
  email: string | null
  role: string
}

export async function listEventStaff(eventId: string): Promise<EventStaff[]> {
  const r = await api<{ staff?: EventStaff[] }>("/api/v1/admin/event-staff", {
    method: "POST",
    body: { eventId },
  })
  return r.staff ?? []
}

// Replace the explicit staff roster of a selected-staff internal channel.
export async function setChannelMembers(
  eventId: string,
  conversationId: string,
  userIds: string[],
): Promise<{ committed?: boolean; members?: Array<{ userId: string; role: string }> }> {
  if (isV2Api) {
    const response = await setEventConversationMembers({
      eventId,
      conversationId,
      personIds: userIds,
    })
    return {
      committed: true,
      members: response.members.map((member) => ({
        userId: member.personId,
        role: member.role ?? "member",
      })),
    }
  }
  return api(`/api/v1/conversations/${conversationId}/set-members`, {
    method: "POST",
    body: { userIds },
  })
}

// Settings save — name / topic / emoji / postingMode / pin go through a raw
// PATCH. archivedAt is guard-protected → archiveConversation action only.
export async function patchChannel(
  eventId: string,
  conversationId: string,
  patch: Partial<Conversation>,
): Promise<Record<string, unknown>> {
  if (isV2Api) {
    const response = await patchEventConversation({
      eventId,
      conversationId,
      patch: {
        ...(patch.name !== undefined ? { title: patch.name } : {}),
        ...(patch.topic !== undefined ? { topic: patch.topic } : {}),
        ...(patch.emoji !== undefined ? { emoji: patch.emoji } : {}),
        ...(patch.postingMode !== undefined ? { postingMode: patch.postingMode } : {}),
        ...(patch.pinnedAtTop !== undefined ? { pinned: Boolean(patch.pinnedAtTop) } : {}),
        ...(patch.staffAudience !== undefined ? { staffAudience: patch.staffAudience } : {}),
      },
    })
    return { conversation: response.conversation }
  }
  return api<Record<string, unknown>>(`/api/v1/conversations/${conversationId}`, {
    method: "PATCH",
    body: patch,
  })
}

export async function archiveChannel(
  eventId: string,
  conversationId: string,
  archived: boolean,
): Promise<{ conversation?: Record<string, unknown> }> {
  if (isV2Api) {
    const response = await archiveEventConversation({ eventId, conversationId, archived })
    return { conversation: response.conversation }
  }
  return api<{ conversation?: Record<string, unknown> }>(
    `/api/v1/event/${eventId}/chat/archive-conversation`,
    { method: "POST", body: { conversationId, archived } },
  )
}

export async function markChannelRead(
  eventId: string,
  conversationId: string,
): Promise<void> {
  if (isV2Api) {
    await markEventConversationRead(eventId, conversationId)
    return
  }
  await api(`/api/v1/event/${eventId}/chat/mark-conversation-read`, {
    method: "POST",
    body: { conversationId },
  })
}

export async function loadAttendeeGroups(eventId: string): Promise<AttendeeGroupLite[]> {
  const r = await api<{ groups?: AttendeeGroupLite[] }>(
    `/api/v1/event/${eventId}/attendee-fields/setup`,
    { method: "POST", body: {} },
  )
  return r.groups ?? []
}

// ── travel-cohort channel suggestions ────────────────────────────────────────
// Powers the New Channel "Flight / Transport" picker. Each suggestion carries a
// (cohortKind, cohortKey) pair the dialog stamps straight onto createChannel.

export type FlightCohortSuggestion = {
  kind: "flight_arrival" | "flight_departure"
  cohortKey: string
  carrierIataCode: string
  flightNumber: string
  airportCode: string
  date: string
  sampleLocalAt: string
  count: number
}
export type FlightWindowSuggestion = {
  kind: "arrival_window" | "departure_window"
  cohortKey: string
  label: string
  airportCode: string
  date: string
  startTime: string
  endTime: string
  count: number
}
export type TransferCohortSuggestion = {
  kind: "ground_transfer"
  cohortKey: string
  mode: string
  direction: string
  vendorName: string
  pickupAt: string
  pickupLocationName: string | null
  count: number
}
export type TransferWindowSuggestion = {
  kind: "transfer_window"
  cohortKey: string
  label: string
  direction: string
  date: string
  startTime: string
  endTime: string
  count: number
}

export type AnyCohortSuggestion =
  | FlightCohortSuggestion
  | FlightWindowSuggestion
  | TransferCohortSuggestion
  | TransferWindowSuggestion

export function flightCohortsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["chat", "flight-cohorts", eventId] as const,
    queryFn: () =>
      api<{ flightCohorts: FlightCohortSuggestion[]; windowSuggestions: FlightWindowSuggestion[] }>(
        `/api/v1/event/${eventId}/chat/suggest-flight-cohorts`,
        { method: "POST", body: { minCount: 2 } },
      ),
  })
}

export function transportCohortsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ["chat", "transport-cohorts", eventId] as const,
    queryFn: () =>
      api<{ transferCohorts: TransferCohortSuggestion[]; windowSuggestions: TransferWindowSuggestion[] }>(
        `/api/v1/event/${eventId}/chat/suggest-transport-cohorts`,
        { method: "POST", body: { minCount: 2 } },
      ),
  })
}

// HH:MM out of a naive `YYYY-MM-DDTHH:MM[:SS]` wall-clock string.
function cohortTimeLabel(localAt: string): string {
  const hhmm = localAt.slice(11, 16)
  if (!hhmm) return ""
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10))
  if (Number.isNaN(h)) return hhmm
  const ampm = h < 12 ? "AM" : "PM"
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`
}

// Human label + suggested channel name for each suggestion kind. Returns
// { title, name } — title for the pick-list row, name to prefill the field.
export function describeCohortSuggestion(
  s: FlightCohortSuggestion | FlightWindowSuggestion | TransferCohortSuggestion | TransferWindowSuggestion,
): { title: string; name: string } {
  switch (s.kind) {
    case "flight_arrival":
    case "flight_departure": {
      const verb = s.kind === "flight_arrival" ? "arriving" : "departing"
      const flight = `${s.carrierIataCode}${s.flightNumber}`
      return {
        title: `${flight} · ${verb} ${s.airportCode} · ${s.date} ${cohortTimeLabel(s.sampleLocalAt)}`,
        name: `${flight} ${s.airportCode}`,
      }
    }
    case "arrival_window":
    case "departure_window": {
      const verb = s.kind === "arrival_window" ? "Arrivals" : "Departures"
      return {
        title: `${verb} · ${s.airportCode} · ${s.date} · ${s.label} (${s.startTime}–${s.endTime})`,
        name: `${s.airportCode} ${s.label} ${verb}`,
      }
    }
    case "ground_transfer": {
      const mode = s.mode.replace(/_/g, " ")
      return {
        title: `${s.vendorName} · ${mode} · ${s.direction} · ${cohortTimeLabel(s.pickupAt)}${
          s.pickupLocationName ? ` · ${s.pickupLocationName}` : ""
        }`,
        name: `${s.vendorName} ${mode}`.trim(),
      }
    }
    case "transfer_window": {
      return {
        title: `${s.direction} transfers · ${s.date} · ${s.label} (${s.startTime}–${s.endTime})`,
        name: `${s.label} ${s.direction} transfers`,
      }
    }
  }
}

// ── display helpers ──────────────────────────────────────────────────────────

// A channel is "guest-facing" when attendees are members: every confirmed guest
// auto-joins, or membership follows an attendee group / a linked agenda item or
// group. Otherwise it's a staff-only (internal) channel. Drives the soft
// eggplant # badge (.ch-guest-ic).
export function isGuestFacing(c: Conversation): boolean {
  return (
    c.includesAllConfirmedGuests === 1 ||
    !!c.attendeeGroupId ||
    !!c.agendaItemId ||
    !!c.agendaGroupId ||
    (!!c.cohortKind && !!c.cohortKey)
  )
}

export function isArchived(c: Conversation): boolean {
  return !!c.archivedAt
}

// An internal channel scoped to an explicit staff roster (vs. all staff).
export function isSelectedStaff(c: Conversation): boolean {
  return !isGuestFacing(c) && c.staffAudience === "selected"
}

// Channel names are stored as slugs (e.g. "vip-lounge"); show "Vip Lounge".
export function properChannelName(name: string | null): string {
  if (!name || !name.trim()) return "Untitled channel"
  return name
    .replace(/[-_]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

export function relTime(iso: string | null): string {
  if (!iso) return ""
  const t = Date.parse(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`)
  if (Number.isNaN(t)) return ""
  const m = Math.round((Date.now() - t) / 60000)
  if (m < 1) return "just now"
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}

export function formatExact(iso: string): string {
  const t = Date.parse(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`)
  if (Number.isNaN(t)) return iso
  return new Date(t).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function v2ConversationToInboxRow(
  conversation: V2ConversationSummary,
): ConversationInboxRow {
  return {
    id: conversation.id,
    eventId: conversation.eventId,
    scope: conversation.kind === "direct" ? "conversation" : "channel",
    name: conversation.title,
    topic: conversation.topic,
    emoji: conversation.emoji ?? null,
    agendaItemId:
      conversation.originScopeKind === "activity" ? conversation.originScopeId : null,
    agendaGroupId: conversation.choiceGroupIds?.[0] ?? null,
    attendeeGroupId: conversation.segmentIds?.[0] ?? null,
    cohortKind: conversation.cohort?.kind ?? null,
    cohortKey: conversation.cohort?.key ?? null,
    includesAllConfirmedGuests: conversation.includeAllConfirmedAttendees ?? false,
    postingMode: conversation.postingMode ??
      (conversation.kind === "announcement" ? "organizers-only" : "members"),
    staffAudience: conversation.staffAudience ?? null,
    pinnedAtTop: conversation.pinned ?? false,
    archivedAt: conversation.status === "archived" ? conversation.closedAt : null,
    createdAt: conversation.historyStartsAt,
    modifiedAt: conversation.lastMessage?.sentAt ?? conversation.historyStartsAt,
    lastMessage: conversation.lastMessage
      ? {
          id: conversation.lastMessage.id,
          authorName: conversation.lastMessage.authorDisplayName,
          authorRole: conversation.lastMessage.authorRole,
          body: conversation.lastMessage.body,
          kind: conversation.lastMessage.messageKind ?? "person",
          createdAt: conversation.lastMessage.sentAt,
        }
      : null,
    unread: conversation.unreadCount,
    mentionsMe: false,
    canPost: conversation.canPost ?? conversation.status === "active",
  }
}

export function v2MessageToChatMessage(message: V2ConversationMessage): ChatMessage {
  return {
    id: message.id,
    conversationId: message.conversationId,
    authorUserId: message.isMine ? getSessionUser()?.id ?? null : null,
    authorGuestId: null,
    authorNameAtSend: message.authorDisplayName ?? "Event Team",
    authorRoleAtSend: message.authorRole,
    body: message.body,
    richText: message.richTextDoc ? JSON.stringify(message.richTextDoc) : null,
    attachments: message.attachmentManifest.length
      ? JSON.stringify(message.attachmentManifest)
      : null,
    kind: message.messageKind,
    urgency: message.urgency ?? "normal",
    createdAt: message.sentAt,
    editedAt: message.editedAt ?? null,
    deletedAt: message.deletedAt ?? null,
    clientId: message.clientId ?? null,
    mentions: message.mentions?.length ? JSON.stringify(message.mentions) : null,
    unfurls: message.unfurls?.length ? JSON.stringify(message.unfurls) : null,
    parentMessageId: message.replyToMessageId,
    replyCount: message.replyCount ?? null,
    lastReplyAt: message.lastReplyAt ?? null,
    reflectedFromThreadRootId: message.reflectedFromThreadRootId ?? null,
  }
}
